import { useState, useEffect, useRef } from "react";
import { 
  FileText, 
  Plus, 
  Trash2,
  Download,
  Folder,
  Search,
  Upload,
  File,
  X,
  CheckCircle,
  Loader2,
  Lock,
  Users,
  Globe,
  Eye,
  EyeOff
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: "ein_irs", label: "EIN/IRS Documents", icon: "📋" },
  { value: "duns_sam", label: "DUNS/SAM.gov", icon: "🏛️" },
  { value: "bylaws", label: "Corporate Bylaws", icon: "📜" },
  { value: "licenses", label: "Licenses/Permits", icon: "📄" },
  { value: "contracts", label: "Contracts", icon: "✍️" },
  { value: "receipts", label: "Receipts", icon: "🧾" },
  { value: "other", label: "Other", icon: "📁" }
];

const Documents = ({ user }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [viewFilter, setViewFilter] = useState("my"); // "my", "team", "all"
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const [docForm, setDocForm] = useState({
    name: "",
    category: "other",
    description: "",
    visibility: "private" // "private" or "team"
  });

  useEffect(() => {
    fetchDocuments();
  }, [viewFilter]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/documents?filter=${viewFilter}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data?.documents ?? []);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      toast.error("Failed to load documents");
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 50MB");
        return;
      }
      setSelectedFile(file);
      if (!docForm.name) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, "");
        setDocForm(prev => ({ ...prev, name: nameWithoutExt }));
      }
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    if (!docForm.name.trim()) {
      toast.error("Please enter a document name");
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("name", docForm.name);
      formData.append("category", docForm.category);
      formData.append("description", docForm.description || "");
      formData.append("visibility", docForm.visibility);

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const response = await fetch(`${API}/documents/upload`, {
        method: "POST",
        credentials: "include",
        body: formData
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        toast.success("Document uploaded successfully!");
        setShowAddDialog(false);
        resetForm();
        fetchDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to upload document");
      }
    } catch {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetForm = () => {
    setDocForm({ name: "", category: "other", description: "", visibility: "private" });
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteDocument = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try {
      const response = await fetch(`${API}/documents/${id}`, { method: "DELETE", credentials: "include" });
      if (response.ok) {
        toast.success("Document deleted");
        fetchDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to delete");
      }
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleToggleVisibility = async (doc) => {
    const newVisibility = doc.visibility === "private" ? "team" : "private";
    try {
      const response = await fetch(`${API}/documents/${doc.id}/visibility`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ visibility: newVisibility })
      });
      
      if (response.ok) {
        toast.success(newVisibility === "team" ? "Document shared with team" : "Document is now private");
        fetchDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update visibility");
      }
    } catch (error) {
      toast.error("Failed to update visibility");
    }
  };

  const handleDownloadDocument = async (doc) => {
    if (doc.file_url) {
      window.open(doc.file_url, '_blank');
      return;
    }
    try {
      window.open(`${API}/documents/${doc.id}/download`, '_blank');
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredDocuments = (documents || []).filter(d => {
    const matchesSearch = d.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === "all" || d.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedDocuments = categories.reduce((acc, cat) => {
    acc[cat.value] = (filteredDocuments || []).filter(d => d.category === cat.value);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#FFD700] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#FFFFFF]" style={{ fontFamily: 'Montserrat, sans-serif', textShadow: '0 0 20px rgba(255, 215, 0, 0.2)' }}>
            Documents Vault
          </h1>
          <p className="text-[#E5C558] mt-1">Your private document storage with optional team sharing</p>
        </div>
        <Dialog open={showAddDialog} onOpenChange={(open) => {
          setShowAddDialog(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button 
              className="bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold hover:from-[#E5C558] hover:to-[#FFD700]"
              data-testid="add-document-btn"
            >
              <Upload size={18} className="mr-2" /> Upload Document
            </Button>
          </DialogTrigger>
          <DialogContent 
            className="sm:max-w-md"
            style={{
              background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.98) 0%, rgba(15, 10, 5, 0.98) 100%)',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              boxShadow: '0 0 50px rgba(255, 215, 0, 0.15)'
            }}
          >
            <DialogHeader>
              <DialogTitle className="text-[#FFFFFF] flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <Upload className="text-[#FFD700]" size={20} />
                Upload Document
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {/* File Drop Zone */}
              <div 
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all hover:border-[#FFD700]"
                style={{
                  borderColor: selectedFile ? '#FFD700' : 'rgba(255, 215, 0, 0.3)',
                  background: selectedFile ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255, 215, 0, 0.05)'
                }}
                onClick={() => fileInputRef.current?.click()}
                data-testid="file-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={handleFileSelect}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv"
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <CheckCircle className="text-[#22C55E]" size={24} />
                    <div className="text-left">
                      <p className="text-[#FFFFFF] font-medium">{selectedFile.name}</p>
                      <p className="text-[#E5C558] text-sm">{formatFileSize(selectedFile.size)}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                      className="text-[#EF4444] hover:bg-[#EF4444]/10"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ) : (
                  <>
                    <File className="text-[#FFD700] mx-auto mb-2" size={32} />
                    <p className="text-[#FFFFFF]">Click to select a file</p>
                    <p className="text-[#E5C558] text-sm mt-1">PDF, Word, Excel, Images (max 50MB)</p>
                  </>
                )}
              </div>

              {/* Document Name */}
              <div>
                <Label className="text-[#E5C558]">Document Name</Label>
                <Input
                  value={docForm.name}
                  onChange={(e) => setDocForm({ ...docForm, name: e.target.value })}
                  placeholder="e.g., 2024 Tax Return"
                  className="bg-[#1A1A1A] border-[#27272A] text-[#FFFFFF] focus:border-[#FFD700]"
                  style={{ background: 'rgba(26, 26, 26, 0.8)' }}
                />
              </div>

              {/* Category */}
              <div>
                <Label className="text-[#E5C558]">Category</Label>
                <Select value={docForm.category} onValueChange={(v) => setDocForm({ ...docForm, category: v })}>
                  <SelectTrigger className="bg-[#1A1A1A] border-[#27272A] text-[#FFFFFF]" style={{ background: 'rgba(26, 26, 26, 0.8)' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value} className="text-[#FFFFFF]">
                        {cat.icon} {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Visibility Toggle */}
              <div>
                <Label className="text-[#E5C558]">Visibility</Label>
                <div className="flex gap-2 mt-2">
                  <Button
                    type="button"
                    variant={docForm.visibility === "private" ? "default" : "outline"}
                    className={`flex-1 ${docForm.visibility === "private" 
                      ? "bg-[#FFD700] text-[#0a0500] hover:bg-[#E5C558]" 
                      : "border-[#27272A] text-[#E5C558] hover:border-[#FFD700]"}`}
                    onClick={() => setDocForm({ ...docForm, visibility: "private" })}
                  >
                    <Lock size={16} className="mr-2" />
                    Private
                  </Button>
                  <Button
                    type="button"
                    variant={docForm.visibility === "team" ? "default" : "outline"}
                    className={`flex-1 ${docForm.visibility === "team" 
                      ? "bg-[#FFD700] text-[#0a0500] hover:bg-[#E5C558]" 
                      : "border-[#27272A] text-[#E5C558] hover:border-[#FFD700]"}`}
                    onClick={() => setDocForm({ ...docForm, visibility: "team" })}
                  >
                    <Users size={16} className="mr-2" />
                    Share with Team
                  </Button>
                </div>
                <p className="text-xs text-[#71717A] mt-2">
                  {docForm.visibility === "private" 
                    ? "Only you can see this document" 
                    : "All team members can view and download"}
                </p>
              </div>

              {/* Description */}
              <div>
                <Label className="text-[#E5C558]">Description (Optional)</Label>
                <Textarea
                  value={docForm.description}
                  onChange={(e) => setDocForm({ ...docForm, description: e.target.value })}
                  placeholder="Brief description..."
                  className="bg-[#1A1A1A] border-[#27272A] text-[#FFFFFF] focus:border-[#FFD700]"
                  style={{ background: 'rgba(26, 26, 26, 0.8)' }}
                  rows={2}
                />
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#E5C558]">Uploading...</span>
                    <span className="text-[#FFD700]">{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-[#27272A] rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all duration-300"
                      style={{ 
                        width: `${uploadProgress}%`,
                        background: 'linear-gradient(90deg, #FFD700, #D4AF37)'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Upload Button */}
              <Button 
                onClick={handleUploadDocument} 
                disabled={uploading || !selectedFile}
                className="w-full bg-gradient-to-r from-[#FFD700] to-[#D4AF37] text-[#0a0500] font-semibold hover:from-[#E5C558] hover:to-[#FFD700] disabled:opacity-50"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={18} className="mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* View Filter Tabs */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex rounded-lg p-1" style={{ background: 'rgba(26, 26, 26, 0.8)', backdropFilter: 'blur(8px)' }}>
          <button
            onClick={() => setViewFilter("my")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
              viewFilter === "my" 
                ? "bg-[#FFD700] text-[#0a0500]" 
                : "text-[#E5C558] hover:text-[#FFFFFF]"
            }`}
          >
            <Lock size={16} />
            My Documents
          </button>
          <button
            onClick={() => setViewFilter("team")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
              viewFilter === "team" 
                ? "bg-[#FFD700] text-[#0a0500]" 
                : "text-[#E5C558] hover:text-[#FFFFFF]"
            }`}
          >
            <Users size={16} />
            Team Shared
          </button>
          <button
            onClick={() => setViewFilter("all")}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
              viewFilter === "all" 
                ? "bg-[#FFD700] text-[#0a0500]" 
                : "text-[#E5C558] hover:text-[#FFFFFF]"
            }`}
          >
            <Globe size={16} />
            All
          </button>
        </div>
        
        <p className="text-sm text-[#71717A]">
          {viewFilter === "my" && "Showing your private and shared documents"}
          {viewFilter === "team" && "Showing documents shared by team members"}
          {viewFilter === "all" && "Showing all documents you have access to"}
        </p>
      </div>

      {/* Search & Category Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#E5C558]" size={20} />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search documents..."
            className="pl-10 text-[#FFFFFF]"
            style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-48 text-[#FFFFFF]" style={{ background: 'rgba(26, 26, 26, 0.8)', borderColor: 'rgba(255, 215, 0, 0.2)' }}>
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
            <SelectItem value="all" className="text-[#FFFFFF]">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value} className="text-[#FFFFFF]">{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category) => {
          const docs = groupedDocuments[category.value];
          if (filterCategory !== "all" && filterCategory !== category.value) return null;
          if (docs.length === 0 && filterCategory === "all") return null;
          
          return (
            <Card 
              key={category.value} 
              style={{
                background: 'linear-gradient(145deg, rgba(20, 15, 5, 0.9) 0%, rgba(15, 10, 5, 0.85) 100%)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 215, 0, 0.15)',
                boxShadow: '0 0 30px rgba(255, 200, 100, 0.08)'
              }}
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-[#FFFFFF] flex items-center gap-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <span>{category.icon}</span>
                  {category.label}
                  <span className="text-xs text-[#E5C558] ml-auto px-2 py-0.5 rounded-full" style={{ background: 'rgba(255, 215, 0, 0.15)' }}>{docs.length}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {docs.map((doc) => (
                    <div 
                      key={doc.id}
                      className="flex items-center justify-between p-3 rounded-lg group transition-all"
                      style={{
                        background: 'rgba(26, 26, 26, 0.6)',
                        border: '1px solid rgba(39, 39, 42, 0.5)',
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                        e.currentTarget.style.background = 'rgba(26, 26, 26, 0.8)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(39, 39, 42, 0.5)';
                        e.currentTarget.style.background = 'rgba(26, 26, 26, 0.6)';
                      }}
                      data-testid={`document-${doc.id}`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText size={18} className="text-[#FFD700] flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-[#FFFFFF] truncate">{doc.name}</p>
                            {doc.visibility === "team" ? (
                              <Users size={12} className="text-[#3B82F6] flex-shrink-0" title="Shared with team" />
                            ) : (
                              <Lock size={12} className="text-[#71717A] flex-shrink-0" title="Private" />
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#E5C558]">
                            {doc.file_size && <span>{formatFileSize(doc.file_size)}</span>}
                            {!doc.is_mine && doc.uploaded_by_name && (
                              <span className="text-[#71717A]">by {doc.uploaded_by_name}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {/* Toggle visibility (only for own docs) */}
                        {doc.is_mine && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleToggleVisibility(doc)}
                            className={doc.visibility === "team" ? "text-[#3B82F6] hover:bg-[#3B82F6]/10" : "text-[#71717A] hover:bg-[#71717A]/10"}
                            title={doc.visibility === "team" ? "Make private" : "Share with team"}
                          >
                            {doc.visibility === "team" ? <EyeOff size={14} /> : <Eye size={14} />}
                          </Button>
                        )}
                        {(doc.file_url || doc.file_data) && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDownloadDocument(doc)}
                            className="text-[#FFD700] hover:bg-[#FFD700]/10"
                          >
                            <Download size={14} />
                          </Button>
                        )}
                        {doc.is_mine && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="text-[#EF4444] hover:bg-[#EF4444]/10"
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                  {docs.length === 0 && (
                    <p className="text-center text-[#E5C558] text-sm py-4">No documents</p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredDocuments.length === 0 && (
        <div className="text-center py-12">
          <Folder size={48} className="text-[#FFD700] mx-auto mb-3 opacity-50" />
          <p className="text-[#E5C558]">No documents found</p>
          <p className="text-[#71717A] text-sm mt-1">
            {viewFilter === "my" ? "Upload your first document to get started" : "No team-shared documents available"}
          </p>
        </div>
      )}
    </div>
  );
};

export default Documents;
